import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import clsx from 'classnames';

import { BrandLogo } from 'assets';
import appleStore from 'assets/images/apple-store@3x.png';
import googleStore from 'assets/images/google-play@3x.png';
import { ReactComponent as CbsSportsDigital } from 'assets/svg/cbs-sports-digital.svg';
import { ReactComponent as FacebookIcon } from 'assets/images/svg/facebook-icon.svg';
import { ReactComponent as TwitterIcon } from 'assets/svg/socials/twitter.svg';
import { ReactComponent as InstagramIcon } from 'assets/images/svg/instagram-icon.svg';
import { ReactComponent as YouTubeIcon } from 'assets/images/svg/youtube-icon.svg';
import { ReactComponent as TiktokIcon } from 'assets/images/svg/tiktok-icon.svg';
import { ReactComponent as PoweredBySG } from 'assets/images/svg/powered-by-sg.svg';

import * as routes from 'utils/routes';
import siteStyle, { M } from 'utils/siteStyle';
import { useDimension } from 'hooks/useDimensions';
import { useSelector } from 'react-redux';
import { features } from 'utils/features';

const Footer = () => {
  const { width } = useDimension();
  const status = useSelector(({ site }) => site?.data);

  const isSubFooterMobileBreakpoint = useMemo(() => {
    if (M.SG) return width < 1000;
    return width < 786;
  }, [width]);

  const renderAppStoreButton = (title, icon, href) => {
    if (!href) return null;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        <img src={icon} alt={title} className="h-9 md:h-10" width="auto" height={40} loading="lazy" />
      </a>
    );
  };

  const renderLink = (label, to, openNewWindow) => {
    if (to.startsWith('http') || to.startsWith('mailto')) {
      return (
        <div className="flex items-center">
          <a
            href={to}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </a>
        </div>
      );
    }
    return (
      <div className="flex items-center">
        <Link to={to} target={openNewWindow ? '_blank' : null}>
          {label}
        </Link>
      </div>
    );
  };

  const renderSocialMediaLink = (provider, link) => {
    if (!link) return null;

    const providers = {
      facebook: { link: 'https://www.facebook.com/', icon: FacebookIcon },
      instagram: { link: 'https://www.instagram.com/', icon: InstagramIcon },
      tiktok: { link: 'https://www.tiktok.com/', icon: TiktokIcon },
      twitter: { link: 'https://twitter.com/', icon: TwitterIcon },
      youtube: { link: 'https://www.youtube.com/', icon: YouTubeIcon },
    };

    const Icon = providers[provider]?.icon;

    return (
      <a
        href={`${providers[provider]?.link}${link}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={provider}
        className={siteStyle({
          common: 'bg-dark h-8 w-8 flex items-center justify-center',
          default: 'rounded-4',
          gd: 'rounded-none',
        })}
      >
        <Icon id={`social-link-icon-${provider}`} className="fill-current text-white" />
      </a>
    );
  };

  const renderLinksSection = (
    <div className={siteStyle({
      common: 'grid grid-cols-1 items-start text-4 leading-5 gap-6',
      foh: 'md:grid-cols-1',
      twc: 'md:grid-cols-1',
      default: 'md:w-1/2 md:grid-cols-2',
    })}
    >
      {renderLink('Get help', routes.SUPPORT)}
      {features.networkMembership && status?.networkMembershipEnabled && (
        <div className="md:hidden">
          {renderLink('Join the membership', routes.NETWORK_MEMBERSHIP_SALES)}
        </div>
      )}
      {features.courseOperatorsVipPerks && renderLink('VIP Perks', routes.VIP_PERKS)}
      {M.SG && renderLink('Buy gift cards', 'https://info.supremegolf.com/supreme-golf-gift-cards/')}
      {features.networkMembership && status?.networkMembershipEnabled && (
        <div className="hidden md:block">
          {renderLink('Join the membership', routes.NETWORK_MEMBERSHIP_SALES)}
        </div>
      )}
      {renderLink('Cancellation policy', routes.CANCELLATION_POLICY)}
      {(M.SG || M.BT) && renderLink('Give feedback', `mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`)}
      {M.BT && renderLink('Course inquiries', routes.GOLF_COURSE_RESOURCES)}
      {M.SG && renderLink('For course owners', 'https://courses.supremegolf.com/?utm_source=supremegolf.com&utm_medium=referral&utm_campaign=homepage')}
      {M.SG && renderLink('Add your golf course', 'https://courses.supremegolf.com/tee-time-distribution/?utm_source=supremegolf.com&utm_medium=referral&utm_campaign=homepage')}
      {(M.GD || M.BT) && renderLink('Add your golf course', routes.FREE_MARKETPLACE)}
      {M.SG && renderLink('Careers', 'https://info.supremegolf.com/careers/', true)}
    </div>
  );
  const showAppStore = process.env.REACT_APP_IOS_APP_LINK || process.env.REACT_APP_GOOGLE_APP_LINK;

  const renderMainFooter = (
    <div className="pt-12 pb-10 md:py-20 flex flex-col md:flex-row gap-10 md:justify-between w-full h-full">
      <div className="md:w-1/2 flex flex-col gap-10 justify-between">
        <div>
          <BrandLogo
            type={M.TWC ? 'broght-to-you-by' : 'dark'}
            className={siteStyle({
              default: 'h-8',
              foh: 'w-58 h-6 lg:w-fit lg:h-8',
              twc: 'h-auto',
            })}
          />
        </div>
        {(M.SG || M.BT || showAppStore) && (
          <div>
            {M.TWC && <p className="font-primary-two text-3 leading-4 text-dark pb-2">Download The Weather Channel app</p>}
            <div className="flex gap-4">
              {renderAppStoreButton('apple-store', appleStore, process.env.REACT_APP_IOS_APP_LINK)}
              {renderAppStoreButton('google-play', googleStore, process.env.REACT_APP_GOOGLE_APP_LINK)}
            </div>
          </div>
        )}
      </div>
      {renderLinksSection}
    </div>
  );

  const renderSocialMediaLinksSection = (process.env.REACT_APP_FACEBOOK_ACCOUNT
    || process.env.REACT_APP_TWITTER_ACCOUNT
    || process.env.REACT_APP_INSTAGRAM_ACCOUNT
    || process.env.REACT_APP_YOUTUBE_ACCOUNT
    || process.env.REACT_APP_TIKTOK_ACCOUNT)
    && (
      <div className="flex gap-6">
        {renderSocialMediaLink('facebook', process.env.REACT_APP_FACEBOOK_ACCOUNT)}
        {renderSocialMediaLink('twitter', process.env.REACT_APP_TWITTER_ACCOUNT)}
        {renderSocialMediaLink('instagram', process.env.REACT_APP_INSTAGRAM_ACCOUNT)}
        {renderSocialMediaLink('youtube', process.env.REACT_APP_YOUTUBE_ACCOUNT)}
        {renderSocialMediaLink('tiktok', process.env.REACT_APP_TIKTOK_ACCOUNT)}
      </div>
    );

  const renderCopyrightSection = (
    <div className={clsx({ 'flex gap-6 text-4 leading-5 text-dark': true, 'flex-col': isSubFooterMobileBreakpoint })}>
      <div className="flex items-center">
        ©
        {' '}
        {moment().year()}
        {' '}
        {M.TWC ? 'Supreme Golf' : process.env.REACT_APP_FRIENDLY_NAME}
      </div>
      <Link
        to={routes.TERMS_AND_CONDITIONS}
        target="_blank"
        className="flex items-center"
      >
        Terms
      </Link>
      <Link
        to={routes.PRIVACY_POLICY}
        target="_blank"
        className="flex items-center"
      >
        Privacy Policy
      </Link>
      {M.SG && (
        <div>
          <CbsSportsDigital />
        </div>
      )}
    </div>
  );

  const renderSubFooter = (
    <div
      className={clsx({
        'w-full border-t border-light-grey-four pt-10': true,
        'pb-20': !isSubFooterMobileBreakpoint,
        'pb-10': isSubFooterMobileBreakpoint,
      })}
    >
      <div
        className={clsx({
          'h-8 w-full flex items-center justify-between': !isSubFooterMobileBreakpoint,
          'flex flex-col gap-6': isSubFooterMobileBreakpoint,
        })}
      >
        <div className="flex flex-col gap-6 md:flex-row">
          {renderSocialMediaLinksSection}
          {!(M.SG || M.BT) && <PoweredBySG />}
        </div>
        {renderCopyrightSection}
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-center bg-white border-t border-light-grey-four px-4 md:px-10 lg:px-14">
      <div className="w-full max-w-300">
        <footer className="flex flex-col items-center w-full text-dark">
          {renderMainFooter}
          {renderSubFooter}
        </footer>
      </div>
    </div>
  );
};

export default Footer;
